import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ArrowLeft, Check, CircleAlert, CircleCheck, Link2, Network, Pencil, Plus, Save, Search, X } from "lucide-react";
import { api } from "./api";
import type { GraphNode, NoteRecord, SnapshotEvent } from "./types";

type PublicType = "character" | "location" | "object" | "community";

const TYPE_OPTIONS: Array<{ value: PublicType; label: string }> = [
  { value: "character", label: "Персонаж" },
  { value: "location", label: "Локация" },
  { value: "object", label: "Предмет" },
  { value: "community", label: "Сообщество" }
];

function publicType(note: NoteRecord): PublicType {
  if (TYPE_OPTIONS.some((option) => option.value === note.type)) return note.type as PublicType;
  if (note.id.startsWith("Локации/")) return "location";
  if (note.id.startsWith("Объекты/")) return "object";
  if (note.id.startsWith("Сообщества/")) return "community";
  return "character";
}

function splitMediaPrefix(body: string): { mediaPrefix: string; text: string } {
  const normalized = body.replace(/\r\n/g, "\n").trim();
  const lines = normalized.split("\n");
  const media: string[] = [];
  while (lines.length > 0 && (!lines[0]?.trim() || /^!\[\[[^\]]+\]\]$/.test(lines[0]!.trim()))) {
    media.push(lines.shift()!);
  }
  return { mediaPrefix: media.join("\n").trim(), text: lines.join("\n").trim() };
}

function inlineNodeMarkdown(node: ChildNode): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (!(node instanceof Element)) return "";
  const tag = node.tagName.toLowerCase();
  const text = [...node.childNodes].map(inlineNodeMarkdown).join("");
  if (tag === "br") return "\n";
  if (tag === "strong" || tag === "b") return `**${text}**`;
  if (tag === "em" || tag === "i") return `*${text}*`;
  if (tag === "code") return `\`${text.replaceAll("`", "\\`")}\``;
  if (tag === "a") {
      const noteId = node.getAttribute("data-note-id");
      if (noteId) {
        const raw = node.getAttribute("data-wiki-raw") ?? noteId;
        const pipe = raw.indexOf("|");
        const target = (pipe >= 0 ? raw.slice(0, pipe) : raw).trim();
        const originalLabel = (pipe >= 0 ? raw.slice(pipe + 1) : raw).trim();
        return text.trim() === originalLabel
          ? `[[${raw}]]`
          : `[[${target}|${text.trim() || noteId.split("/").at(-1)}]]`;
      }
      const href = node.getAttribute("href") ?? "";
      return href && href !== "#" ? (text === href ? href : `[${text}](${href})`) : text;
  }
  return text;
}

function inlineMarkdown(element: Element): string {
  return [...element.childNodes].map(inlineNodeMarkdown).join("");
}

function listMarkdown(element: Element, ordered: boolean): string {
  return [...element.children].filter((child) => child.tagName.toLowerCase() === "li").map((item, index) => {
    const own = [...item.childNodes]
      .filter((node) => !(node instanceof Element && ["ul", "ol"].includes(node.tagName.toLowerCase())))
      .map(inlineNodeMarkdown)
      .join("").trim();
    const nested = [...item.children].filter((child) => ["ul", "ol"].includes(child.tagName.toLowerCase()))
      .map((child) => listMarkdown(child, child.tagName.toLowerCase() === "ol").split("\n").map((line) => `  ${line}`).join("\n"))
      .join("\n");
    return `${ordered ? `${index + 1}.` : "-"} ${own}${nested ? `\n${nested}` : ""}`;
  }).join("\n");
}

function blockMarkdown(node: ChildNode): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").trim();
  if (!(node instanceof Element)) return "";
  const tag = node.tagName.toLowerCase();
  if (tag === "img") return "";
  if (tag === "p") return inlineMarkdown(node).trim();
  if (/^h[1-6]$/.test(tag)) return `${"#".repeat(Number(tag[1]))} ${inlineMarkdown(node).trim()}`;
  if (tag === "ul" || tag === "ol") return listMarkdown(node, tag === "ol");
  if (tag === "pre") return `\`\`\`\n${node.textContent ?? ""}\n\`\`\``;
  if (tag === "hr") return "---";
  if (tag === "blockquote") {
    return [...node.childNodes].map(blockMarkdown).filter(Boolean).join("\n\n")
      .split("\n").map((line) => `> ${line}`).join("\n");
  }
  if (tag === "details") {
    const summary = [...node.children].find((child) => child.tagName.toLowerCase() === "summary");
    const type = [...node.classList].find((name) => name.startsWith("callout-"))?.slice(8) || "info";
    const fold = node.getAttribute("data-callout-fold") || "-";
    const body = [...node.childNodes]
      .filter((child) => child !== summary)
      .map(blockMarkdown).filter(Boolean).join("\n\n");
    const lines = [`> [!${type}]${fold} ${summary ? inlineMarkdown(summary).trim() : "Подробнее"}`];
    if (body) lines.push(...body.split("\n").map((line) => `> ${line}`));
    return lines.join("\n");
  }
  return [...node.childNodes].map(blockMarkdown).filter(Boolean).join("\n\n");
}

function editorMarkdown(root: HTMLElement): string {
  return [...root.childNodes].map(blockMarkdown).filter(Boolean).join("\n\n")
    .replace(/\n{3,}/g, "\n\n").trim();
}

const RichTextEditor = memo(forwardRef<HTMLDivElement, {
  html: string;
  disabled: boolean;
  onMarkdownChange: (markdown: string) => void;
}>(function RichTextEditor({ html, disabled, onMarkdownChange }, forwardedRef) {
  const rootRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(forwardedRef, () => rootRef.current as HTMLDivElement, []);
  useEffect(() => {
    if (rootRef.current) rootRef.current.innerHTML = html;
  }, [html]);
  return (
    <div
      ref={rootRef}
      className="markdown-body inline-rich-editor"
      contentEditable={!disabled}
      suppressContentEditableWarning
      spellCheck
      role="textbox"
      aria-multiline="true"
      aria-label="Текст заметки"
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("a")) event.preventDefault();
      }}
      onInput={(event) => onMarkdownChange(editorMarkdown(event.currentTarget))}
    />
  );
}));

function removeOutgoingLinks(body: string, note: NoteRecord, removedIds: Set<string>): string {
  let result = body;
  for (const link of note.outgoing) {
    if (!link.id || !removedIds.has(link.id)) continue;
    result = result.split(`[[${link.raw}]]`).join(link.label);
  }
  return result;
}

function addOutgoingLinks(body: string, nodes: GraphNode[], addedIds: string[]): string {
  if (addedIds.length === 0) return body;
  const rows = addedIds.map((id) => {
    const node = nodes.find((item) => item.id === id);
    return `- [[${id}|${node?.title ?? id.split("/").at(-1)}]]`;
  });
  const connectionHeading = /^##\s+Связи\s*$/m;
  if (connectionHeading.test(body)) return body.replace(connectionHeading, (heading) => `${heading}\n\n${rows.join("\n")}`);
  return `${body.trim()}\n\n## Связи\n\n${rows.join("\n")}`;
}

const EDIT_STATUS_LABELS: Record<NonNullable<NoteRecord["editStatus"]>, string> = {
  pending_review: "Пользовательская правка ожидает проверки",
  reviewing: "Правка проверяется",
  needs_evidence: "Правка ждёт перепросмотра видео",
  conflict: "Правка расходится с источниками и ждёт решения",
  accepted: "Правка подтверждена"
};

export function NoteView({ id, version, event, editing, nodes, onGraph, onNavigate, onEdit, onCloseEdit }: { id: string; version?: number; event?: SnapshotEvent; editing: boolean; nodes: GraphNode[]; onGraph: () => void; onNavigate: (id: string) => void; onEdit: () => void; onCloseEdit: () => void }) {
  const [note, setNote] = useState<NoteRecord>();
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [editSaved, setEditSaved] = useState(false);
  const articleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!editing) setEditSaved(false);
  }, [editing, id]);

  useEffect(() => {
    setState("loading");
    void api.note(id, version).then((result) => {
      if ("redirect" in result) {
        onNavigate(result.redirect);
        return;
      }
      setNote(result);
      setState("ready");
      document.title = `${result.title} — Зазеркалье`;
    }).catch((error) => setState(error instanceof Error && error.message === "not_found" ? "missing" : "error"));
  }, [id, version, onNavigate]);

  useEffect(() => {
    if (!event) return;
    const moved = event.moved.find((item) => item.from === id);
    if (moved) onNavigate(moved.to);
    if (event.removed.includes(id)) setState("missing");
  }, [event, id, onNavigate]);

  useEffect(() => {
    const article = articleRef.current;
    if (!article) return;
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest<HTMLAnchorElement>("a.internal-link");
      if (!anchor) return;
      if (anchor.classList.contains("is-unresolved")) {
        event.preventDefault();
        return;
      }
      const target = anchor.dataset.noteId;
      if (target) {
        event.preventDefault();
        onNavigate(target);
      }
    };
    article.addEventListener("click", onClick);
    return () => article.removeEventListener("click", onClick);
  }, [note, onNavigate]);

  if (state === "loading") return <main className="note-shell"><div className="note-loading"><span />Загружаем заметку…</div></main>;
  if (state === "missing" || state === "error" || !note) return (
    <main className="note-shell empty-note">
      <CircleAlert size={28} />
      <h1>{state === "missing" ? "Заметка удалена или не найдена" : "Не удалось открыть заметку"}</h1>
      <p>Граф продолжает работать с последней согласованной версией.</p>
      <button className="primary-button" onClick={onGraph}><Network size={16} /> Вернуться к графу</button>
    </main>
  );

  if (editing) return <InlineEditor key={`${note.id}:${note.contentHash}`} note={note} nodes={nodes} saved={editSaved} onSaved={() => { setEditSaved(true); onCloseEdit(); }} onGraph={onGraph} onCancel={onCloseEdit} />;

  return (
    <main className="note-shell">
      <header className="note-toolbar">
        <button className="toolbar-action" onClick={() => history.back()}><ArrowLeft size={16} /> Назад</button>
        <button className="toolbar-action" onClick={onGraph}><Network size={16} /> Граф</button>
        <button className="toolbar-action edit-action" onClick={onEdit}><Pencil size={15} /> Правка</button>
      </header>
      <article ref={articleRef} className="note-reader">
        <div className="note-path">{note.id}</div>
        <h1>{note.title}</h1>
        {note.editStatus ? (
          <div className={`edit-review-state state-${note.editStatus}`}>
            {EDIT_STATUS_LABELS[note.editStatus]}
          </div>
        ) : null}
        {note.editStatus && note.editStatus !== "pending_review" && note.editMessage ? (
          <p className="edit-review-message">{note.editMessage}</p>
        ) : null}
        {note.aliases.length > 0 && <div className="aliases">{note.aliases.map((alias) => <span key={alias}>{alias}</span>)}</div>}
        <div className="markdown-body" dangerouslySetInnerHTML={{ __html: note.html }} />
      </article>
    </main>
  );
}

function InlineEditor({ note, nodes, saved, onSaved, onGraph, onCancel }: { note: NoteRecord; nodes: GraphNode[]; saved: boolean; onSaved: () => void; onGraph: () => void; onCancel: () => void }) {
  const initial = useMemo(() => splitMediaPrefix(note.body), [note.id, note.contentHash]);
  const editorRef = useRef<HTMLDivElement>(null);
  const initialConnections = useMemo(
    () => [...new Set(note.outgoing.flatMap((link) => link.resolved && link.id ? [link.id] : []))],
    [note.id, note.contentHash]
  );
  const [title, setTitle] = useState(note.title);
  const [type, setType] = useState<PublicType>(() => publicType(note));
  const [aliases, setAliases] = useState(note.aliases);
  const [aliasDraft, setAliasDraft] = useState("");
  const [editedText, setEditedText] = useState(initial.text);
  const [bodyDirty, setBodyDirty] = useState(false);
  const [connections, setConnections] = useState(initialConnections);
  const [connectionQuery, setConnectionQuery] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string>();
  const published = sent || saved;
  const handleBodyChange = useCallback((markdown: string) => {
    setEditedText(markdown);
    setBodyDirty(true);
  }, []);

  const connectionResults = useMemo(() => {
    const query = connectionQuery.trim().toLocaleLowerCase("ru-RU");
    if (!query) return [];
    return nodes.filter((node) => node.id !== note.id && !connections.includes(node.id)
      && `${node.title} ${node.id}`.toLocaleLowerCase("ru-RU").includes(query)).slice(0, 7);
  }, [connectionQuery, connections, nodes, note.id]);
  const initialBody = note.body.trim();
  const currentBody = bodyDirty
    ? [initial.mediaPrefix, editedText].filter(Boolean).join("\n\n").trim()
    : initialBody;
  const unchanged = title.trim() === note.title
    && type === publicType(note)
    && JSON.stringify(aliases) === JSON.stringify(note.aliases)
    && currentBody === initialBody
    && JSON.stringify(connections) === JSON.stringify(initialConnections);
  const hasPrompt = Boolean(comment.trim());
  const cannotSubmit = (unchanged && !hasPrompt) || sending || !title.trim();

  const addAlias = () => {
    const value = aliasDraft.normalize("NFC").trim();
    if (!value || aliases.includes(value) || aliases.length >= 24) return;
    setAliases([...aliases, value]);
    setAliasDraft("");
  };

  const submit = async () => {
    if (cannotSubmit) return;
    setSending(true);
    setError(undefined);
    const removed = new Set(initialConnections.filter((id) => !connections.includes(id)));
    const added = connections.filter((id) => !initialConnections.includes(id));
    const proposedBody = addOutgoingLinks(removeOutgoingLinks(currentBody, note, removed), nodes, added);
    try {
      const editId = `edit_${crypto.randomUUID().replaceAll("-", "")}`;
      await api.submitSuggestion(editId, note.id, note.contentHash, title.trim(), type, aliases, proposedBody, comment);
      setSent(true);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось отправить правку.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="note-shell">
      <header className="note-toolbar">
        <button className="toolbar-action" onClick={onGraph}><Network size={16} /> Граф</button>
        <button className="toolbar-action" onClick={onCancel}>Отмена</button>
        <button className="primary-button edit-action" disabled={cannotSubmit} onClick={() => void submit()}>
          <Save size={15} /> {sending ? "Отправляем…" : unchanged ? "Отправить" : "Сохранить"}
        </button>
      </header>
      <article className="note-reader note-editing">
        {published ? (
          <div className="inline-success">
            <CircleCheck size={20} />
            <div><strong>Правка опубликована.</strong> Она уже видна на сайте и передана ревьюеру лора.</div>
            <button className="toolbar-action" onClick={onCancel}>Готово</button>
          </div>
        ) : null}
        <div className="note-path">{note.id}</div>
        <input
          className="inline-title"
          value={title}
          maxLength={140}
          onChange={(event) => setTitle(event.target.value)}
          aria-label="Название заметки"
          title="Нажмите и исправьте название"
        />
        <label className="inline-type-row">
          Тип ноды
          <select value={type} onChange={(event) => setType(event.target.value as PublicType)}>
            {TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <div className="aliases alias-editor">
          {aliases.map((alias) => (
            <span className="editor-chip" key={alias}>{alias}<button onClick={() => setAliases(aliases.filter((item) => item !== alias))} aria-label={`Удалить имя ${alias}`}><X size={13} /></button></span>
          ))}
          <span className="alias-add">
            <input value={aliasDraft} onChange={(event) => setAliasDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addAlias(); } }} placeholder="Добавить имя" maxLength={120} aria-label="Добавить имя" />
            <button onClick={addAlias} disabled={!aliasDraft.trim()} aria-label="Добавить имя"><Plus size={15} /></button>
          </span>
        </div>
        {note.image ? <div className="inline-cover"><img src={note.image.url} srcSet={note.image.srcSet} alt={note.image.alt} /></div> : null}
        <div className="inline-rich-editor-wrap">
          <div className="inline-edit-hint"><Pencil size={13} /> Нажмите на текст и редактируйте как обычную страницу</div>
          <RichTextEditor
            ref={editorRef}
            html={note.html}
            disabled={published}
            onMarkdownChange={handleBodyChange}
          />
        </div>
        <button className="inline-add-section" onClick={() => {
          const root = editorRef.current;
          if (!root) return;
          const heading = document.createElement("h2");
          heading.textContent = "Новый раздел";
          const paragraph = document.createElement("p");
          paragraph.textContent = "Введите текст раздела";
          root.append(heading, paragraph);
          setEditedText(editorMarkdown(root));
          setBodyDirty(true);
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(heading);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }}><Plus size={14} /> Добавить раздел</button>
        <section className="inline-connections" aria-label="Связи">
          <div className="inline-block-title"><Link2 size={15} /><strong>Связи</strong><span>{connections.length}</span></div>
          <div className="connection-search">
            <Search size={14} />
            <input value={connectionQuery} onChange={(event) => setConnectionQuery(event.target.value)} placeholder="Найти ноду…" aria-label="Найти ноду" />
          </div>
          {connectionResults.length > 0 && <div className="connection-results">
            {connectionResults.map((node) => <button key={node.id} onClick={() => { setConnections([...connections, node.id]); setConnectionQuery(""); }}><Plus size={13} /><span>{node.title}<small>{node.id.split("/")[0]}</small></span></button>)}
          </div>}
          <div className="connection-thread">
            {connections.length === 0 ? <p>Пока нет исходящих связей.</p> : connections.map((id) => {
              const node = nodes.find((item) => item.id === id);
              return (
                <div className="connection-node" key={id}>
                  <i />
                  <span>{node?.title ?? id.split("/").at(-1)}<small>{id.split("/")[0]}</small></span>
                  <button onClick={() => setConnections(connections.filter((item) => item !== id))} aria-label={`Убрать связь`}><X size={14} /></button>
                </div>
              );
            })}
          </div>
          {note.backlinks.length > 0 && <div className="incoming-links"><span>Ссылаются сюда</span>{note.backlinks.slice(0, 8).map((link) => <em key={link.id}><Check size={11} />{link.title}</em>)}</div>}
        </section>
        <section className="inline-comment" aria-label="Запрос на проверку">
          <div className="inline-block-title"><strong>Что проверить или изменить?</strong><span>необязательно при ручной правке</span></div>
          <p className="prompt-help">Можно не редактировать заметку вручную — опишите мысль, связь или сомнение своими словами.</p>
          <textarea value={comment} onChange={(event) => setComment(event.target.value)} maxLength={1000} rows={4} placeholder="Например: проверь, связан ли этот персонаж с Бабой Жулей, и если это подтверждается — обнови нужные ноды и лор." aria-label="Что проверить или изменить" />
          <p className="review-note">Это подсказка для проверки, а не безусловная команда. Модель сверит её с источниками и контекстом; изменения применятся только при достаточной уверенности.</p>
        </section>
        {error ? <p className="editor-error">{error}</p> : null}
        <div className="inline-save-row">
          <button className="toolbar-action" onClick={onCancel}>Отмена</button>
          <button className="primary-button" disabled={cannotSubmit} onClick={() => void submit()}>
            <Save size={15} /> {sending ? "Отправляем…" : unchanged ? "Отправить на проверку" : "Сохранить правку"}
          </button>
        </div>
      </article>
    </main>
  );
}
